/**
 * Format Parser — imports flight plans from ICAO FPL, Garmin FPL, GPX, KML, GeoJSON.
 * Platform-agnostic: pure string parsing.
 */
import type { FlightPlan, ExportFormat, RouteWaypoint, ResolvedWaypoint } from './types';
import { FlightPlanError } from './errors';

export class FormatParser {
  parse(data: string, format: ExportFormat): Partial<FlightPlan> {
    switch (format) {
      case 'icao-fpl': return this.parseIcaoFpl(data);
      case 'garmin-fpl': return this.parseGarminFpl(data);
      case 'gpx': return this.parseGpx(data);
      case 'kml': return this.parseKml(data);
      case 'geojson': return this.parseGeoJson(data);
      default: throw new FlightPlanError(`Unknown import format: ${format}`, 'PARSE_ERROR');
    }
  }

  detectFormat(data: string): ExportFormat | null {
    const trimmed = data.trim();
    if (trimmed.startsWith('(FPL')) return 'icao-fpl';
    if (trimmed.includes('garmin.com/xmlschemas/FlightPlan')) return 'garmin-fpl';
    if (trimmed.includes('<gpx')) return 'gpx';
    if (trimmed.includes('<kml')) return 'kml';
    try { const j = JSON.parse(trimmed); if (j.type === 'FeatureCollection') return 'geojson'; } catch { /* not JSON */ }
    return null;
  }

  private parseIcaoFpl(data: string): Partial<FlightPlan> {
    const depMatch = data.match(/-([A-Z]{4})\d{4}\n/);
    const destMatch = data.match(/-([A-Z]{4})\d{4}\)?\s*$/m);
    const rulesMatch = data.match(/-(I|V|Y|Z)/);
    const routeMatch = data.match(/[NAM]\d{4}[AF]\d{3}\s+(.+)$/m);

    const departure = depMatch ? this.makeSimpleWaypoint(depMatch[1]) : undefined;
    const destination = destMatch ? this.makeSimpleWaypoint(destMatch[1]) : undefined;
    const flightRules = rulesMatch && (rulesMatch[1] === 'I' || rulesMatch[1] === 'Y') ? 'IFR' as const : 'VFR' as const;

    const intermediates: RouteWaypoint[] = [];
    if (routeMatch) {
      const wps = routeMatch[1].split(/\s+/).filter(w => w !== 'DCT' && w.length >= 2 && w.length <= 5);
      wps.forEach((id, i) => intermediates.push({
        identifier: id, name: id, type: 'fix', latitude: 0, longitude: 0,
        source: 'designated_point', sequenceIndex: i + 1, plannedAltitude: 0,
      }));
    }

    return {
      departure: departure as ResolvedWaypoint,
      destination: destination as ResolvedWaypoint,
      flightRules,
      waypoints: [
        ...(departure ? [{ ...departure, sequenceIndex: 0, plannedAltitude: 0 } as RouteWaypoint] : []),
        ...intermediates,
        ...(destination ? [{ ...destination, sequenceIndex: intermediates.length + 1, plannedAltitude: 0 } as RouteWaypoint] : []),
      ],
    };
  }

  private parseGarminFpl(data: string): Partial<FlightPlan> {
    const wps: RouteWaypoint[] = [];
    const wpRegex = /<waypoint>\s*<identifier>([^<]+)<\/identifier>[\s\S]*?<lat>([^<]+)<\/lat>\s*<lon>([^<]+)<\/lon>/g;
    let match; let idx = 0;
    while ((match = wpRegex.exec(data)) !== null) {
      wps.push({
        identifier: match[1], name: match[1], type: 'fix', latitude: Number(match[2]),
        longitude: Number(match[3]), source: 'designated_point', sequenceIndex: idx++, plannedAltitude: 0,
      });
    }
    return this.buildPartialPlan(wps);
  }

  private parseGpx(data: string): Partial<FlightPlan> {
    const wps: RouteWaypoint[] = [];
    const rteptRegex = /<rtept\s+lat="([^"]+)"\s+lon="([^"]+)"[\s\S]*?<name>([^<]*)<\/name>[\s\S]*?<\/rtept>/g;
    let match; let idx = 0;
    while ((match = rteptRegex.exec(data)) !== null) {
      wps.push({
        identifier: match[3], name: match[3], type: 'fix', latitude: Number(match[1]),
        longitude: Number(match[2]), source: 'designated_point', sequenceIndex: idx++, plannedAltitude: 0,
      });
    }
    if (wps.length === 0) {
      const wptRegex = /<wpt\s+lat="([^"]+)"\s+lon="([^"]+)"[\s\S]*?<name>([^<]*)<\/name>[\s\S]*?<\/wpt>/g;
      while ((match = wptRegex.exec(data)) !== null) {
        wps.push({
          identifier: match[3], name: match[3], type: 'fix', latitude: Number(match[1]),
          longitude: Number(match[2]), source: 'designated_point', sequenceIndex: idx++, plannedAltitude: 0,
        });
      }
    }
    return this.buildPartialPlan(wps);
  }

  private parseKml(data: string): Partial<FlightPlan> {
    const coordMatch = data.match(/<coordinates>([\s\S]*?)<\/coordinates>/);
    if (!coordMatch) return {};
    const wps: RouteWaypoint[] = coordMatch[1].trim().split(/\s+/).map((c, i) => {
      const [lon, lat] = c.split(',').map(Number);
      return {
        identifier: `WP${i}`, name: `WP${i}`, type: 'fix' as const,
        latitude: lat, longitude: lon, source: 'designated_point' as const,
        sequenceIndex: i, plannedAltitude: 0,
      };
    });
    return this.buildPartialPlan(wps);
  }

  private parseGeoJson(data: string): Partial<FlightPlan> {
    const geojson = JSON.parse(data);
    const wps: RouteWaypoint[] = []; let idx = 0;
    for (const f of geojson.features || []) {
      if (f.geometry?.type === 'Point') {
        const [lon, lat] = f.geometry.coordinates;
        wps.push({
          identifier: f.properties?.identifier || `WP${idx}`,
          name: f.properties?.name || `WP${idx}`,
          type: f.properties?.type || 'fix',
          latitude: lat, longitude: lon,
          source: 'designated_point', sequenceIndex: idx++, plannedAltitude: 0,
        });
      }
    }
    return this.buildPartialPlan(wps);
  }

  private buildPartialPlan(wps: RouteWaypoint[]): Partial<FlightPlan> {
    if (wps.length === 0) return {};
    return { departure: wps[0] as ResolvedWaypoint, destination: wps[wps.length - 1] as ResolvedWaypoint, waypoints: wps };
  }

  private makeSimpleWaypoint(id: string): ResolvedWaypoint {
    return { identifier: id, name: id, type: 'airport', latitude: 0, longitude: 0, source: 'nasr' };
  }
}
